import { useState } from "react";
import { Link } from "react-router-dom";

import {
  actorEmail,
  auditStamp,
  auditTarget,
  blockedUserCount,
  eventsOf,
  metricPath,
  metricValue,
  tenantsOf,
  type FleetMetricEnvelope,
  type McpTenantList,
} from "../../api/mcp";
import { BlockControl } from "../../components/mcp/BlockControl";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../../components/states";
import { Badge, Card, Empty, Notice, Page, Panel, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Button, Input } from "../../f7";
import { useAuditFeed } from "../../hooks/useAuditFeed";
import { useResource } from "../../hooks/useResource";
import { useTenantDetails } from "../../hooks/useTenantDetails";
import { ProductPanel, ProductScope } from "../../products/PerProduct";

/**
 * Who is blocked, at what scope.
 *
 * Three scopes exist and they are NOT interchangeable — the whole value of
 * this page is keeping them apart:
 *
 *   merchant-wide      ("<ref>", "*")   nobody at this merchant
 *   user at a merchant ("<ref>", email) one person, one merchant
 *   user everywhere    ("*", email)     one person, every merchant
 *
 * Unblocking at one scope never lifts another, so an operator who cannot see
 * which scope applies will try the wrong lever and conclude the API is broken.
 */
export function Access({ config }: { config: AppConfig }) {
  const { state, reload } = useResource<McpTenantList>(config, "/mcp/admin/tenants");
  // The fleet-wide count, from its own metric rather than summed client-side —
  // the list this page reads is capped to merchants seen in the last year, so
  // a client-side sum would silently undercount against the server's own
  // figure whenever a dormant, blocked merchant fell outside that window.
  const blockedMetric = useResource<FleetMetricEnvelope<"blocked">>(
    config,
    metricPath("blocked"),
  );
  // Who blocked whom, when, and why — absent from every table on this page
  // otherwise, since a blocklist row is deleted on unblock and carries no
  // history of its own.
  const blockAudit = useAuditFeed(config, "tenant");
  // Null until there is a real pair of numbers — see `metricValue`.
  const blockedCounts = metricValue(blockedMetric.state);
  const [email, setEmail] = useState("");
  const [ref, setRef] = useState("");
  const [blockEmail, setBlockEmail] = useState("");

  // Only the merchants that actually need a fan-out: a small, bounded set
  // (blocked_users_count > 0) that the list response already lacks names for.
  // A merchant still carrying `blocked_users` from the list needs no second
  // call. Computed here, unconditionally, so the hook itself is never called
  // conditionally on `state.kind`.
  // Merchant-wide blocks are fanned out too — the list carries `blocked`
  // itself, but not `blocked_reason`/`blocked_by`/`blocked_since`, and those
  // three are exactly what an operator needs to know before touching the
  // block.
  const needsFanOut =
    state.kind === "ready"
      ? [
          ...new Set([
            ...tenantsOf(state.data)
              .filter((t) => blockedUserCount(t) > 0 && !t.blocked_users)
              .map((t) => t.business_reference),
            ...tenantsOf(state.data).filter((t) => t.blocked).map((t) => t.business_reference),
          ]),
        ]
      : [];
  const { byRef: fannedOutDetails } = useTenantDetails(config, needsFanOut);

  // Trimmed once, so the enable check and the URL cannot drift apart. Both of
  // these arrive by paste — from a spreadsheet, or out of Slack — and a
  // trailing space is the normal case rather than the odd one.
  const checkRef = ref.trim();
  const checkEmail = email.trim();

  return (
    <Page title="Blocks & suspensions" subtitle="Who is refused, and at what scope.">

      {/* "Blocks & suspensions" is two mechanisms in two products, and only one
          of them is here. A block is MCP's blocklist row and stops tool calls;
          a suspension is Labeeb's and stops the assistant answering at all. An
          operator who blocks a merchant here and assumes they have been cut off
          has cut off half of it — which is exactly the mistake this line
          exists to prevent. */}
      <ProductScope owns={["mcp"]}>
        a block stops MCP tool calls; suspending the assistant itself is Labeeb&rsquo;s and is not
        wired
      </ProductScope>

      {/* The fleet-wide figure, from its own metric — not summed from the
          tables below, which only cover merchants seen in the last year. */}
      {blockedCounts && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          <Badge tone={blockedCounts.merchants > 0 ? "amber" : "green"}>
            {blockedCounts.merchants} merchant
            {blockedCounts.merchants === 1 ? "" : "s"} blocked fleet-wide
          </Badge>
          <Badge tone={blockedCounts.users_everywhere > 0 ? "amber" : "green"}>
            {blockedCounts.users_everywhere} user
            {blockedCounts.users_everywhere === 1 ? "" : "s"} blocked everywhere
          </Badge>
          <span>
            from <span className="ref">GET /admin/metrics/blocked</span>, independent of the
            merchant list below
          </span>
        </div>
      )}

      {state.kind === "loading" && <Loading what="blocks" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="the block list" message={state.error.message} onRetry={reload} />
      )}
      {state.kind === "notfound" && <LoadFailed what="blocks" message="Route not found." />}
      {state.kind === "invalid" && (
        <LoadFailed what="blocks" message={state.error.message} />
      )}
      {state.kind === "error" && <LoadFailed what="blocks" message={state.error.message} />}

      {state.kind === "ready" &&
        (() => {
          const d = state.data;
          const list = tenantsOf(d);
          const blockedMerchants = list.filter((t) => t.blocked);
          const withUserBlocks = list.filter((t) => blockedUserCount(t) > 0);
          const everywhere = d.users_blocked_across_all_merchants ?? [];
          const nothing =
            blockedMerchants.length === 0 && withUserBlocks.length === 0 && everywhere.length === 0;

          return (
            <>
              {nothing && (
                <Notice title="Nobody is blocked">
                  No merchant-wide blocks, no per-user blocks, and nobody blocked across all
                  merchants.
                </Notice>
              )}

              {/* The design's card grid, not a stack of bare sections under
                  uppercase micro-headings. Each scope is a panel of the same
                  shape as every other panel in the console; the micro-heading
                  was a fifth heading level that appeared on this page only, and
                  it left every table sitting directly on the page with nothing
                  bounding it. */}
              <div className="grid gap-4 lg:grid-cols-2">
                {/* First, because it is the widest scope and belongs to no
                    merchant — attaching it per-merchant would misreport it. */}
                <Card
                  title="Blocked across every merchant"
                  note={
                    <>
                      A <span className="ref">("*", email)</span> row. These people are refused
                      everywhere, including at merchants not listed here. Unblocking them at one
                      merchant will not lift this.
                    </>
                  }
                >
                  {everywhere.length === 0 ? (
                    <Empty>Nobody.</Empty>
                  ) : (
                    <Table
                      className="[--gutter:0px]"
                      head={<tr><Th>Email</Th><Th right>Action</Th></tr>}
                    >
                      {everywhere.map((e) => (
                        <tr key={e}>
                          <Td>
                            <span className="ref">{e}</span>
                          </Td>
                          <Td right>
                            <BlockControl
                              config={config}
                              scope={{ kind: "everywhere", email: e }}
                              blocked
                              onDone={reload}
                            />
                          </Td>
                        </tr>
                      ))}
                    </Table>
                  )}
                </Card>

                <Card
                  title="Blocked merchants"
                  note={
                    <>
                      A <span className="ref">("&lt;reference&gt;", "*")</span> row — every user
                      of that merchant is refused.
                    </>
                  }
                >
                  {blockedMerchants.length === 0 ? (
                    <Empty>None.</Empty>
                  ) : (
                    <Table
                      className="[--gutter:0px]"
                      head={
                        <tr>
                          <Th>Reference</Th>
                          <Th>Name</Th>
                          <Th>Why</Th>
                        </tr>
                      }
                    >
                      {blockedMerchants.map((t) => {
                        const detail = fannedOutDetails[t.business_reference];
                        return (
                          <tr key={t.business_reference}>
                            <Td>
                              <Link
                                to={`/tenants/${encodeURIComponent(t.business_reference)}`}
                                className="ref"
                              >
                                {t.business_reference}
                              </Link>
                            </Td>
                            <Td>{t.business_name}</Td>
                            <Td>
                              {detail?.blocked_reason ? (
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                  {detail.blocked_reason}
                                  {detail.blocked_by && (
                                    <span className="ref ml-1">by {detail.blocked_by}</span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                              )}
                            </Td>
                          </tr>
                        );
                      })}
                    </Table>
                  )}
                </Card>
              </div>

              <Card
                title="Users blocked at one merchant"
                note={
                  <>
                    A <span className="ref">("&lt;reference&gt;", email)</span> row — that person
                    is refused at that merchant only.
                  </>
                }
              >
                {withUserBlocks.length === 0 ? (
                  <Empty>None.</Empty>
                ) : (
                  <Table
                    className="[--gutter:0px]"
                    head={
                      <tr>
                        <Th>Merchant</Th>
                        <Th>Blocked users</Th>
                      </tr>
                    }
                  >
                    {withUserBlocks.map((t) => {
                      // Names from the list when it still carries them, else
                      // from the per-merchant fan-out this page ran for
                      // exactly this reason — MCP moved names off the list
                      // onto tenant detail (8127fde), and dev already made
                      // the move.
                      const names = t.blocked_users ?? fannedOutDetails[t.business_reference]?.blocked_users;

                      return (
                        <tr key={t.business_reference}>
                          <Td>
                            <Link
                              to={`/tenants/${encodeURIComponent(t.business_reference)}/users`}
                              className="ref"
                            >
                              {t.business_reference}
                            </Link>
                            <div className="text-xs text-slate-500 dark:text-slate-400">{t.business_name}</div>
                          </Td>
                          <Td>
                            {!names ? (
                              <Link
                                to={`/tenants/${encodeURIComponent(t.business_reference)}/users`}
                                className="text-xs"
                              >
                                {blockedUserCount(t)} blocked user
                                {blockedUserCount(t) === 1 ? "" : "s"} — open the merchant to see
                                who
                              </Link>
                            ) : (
                              <ul className="space-y-0.5">
                                {names.map((e) => (
                                  <li key={e}>
                                    <Link
                                      to={`/tenants/${encodeURIComponent(
                                        t.business_reference,
                                      )}/users/${encodeURIComponent(e)}`}
                                      className="ref text-xs"
                                    >
                                      {e}
                                    </Link>
                                    {everywhere.includes(e) && (
                                      <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">
                                        also blocked everywhere
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </Td>
                        </tr>
                      );
                    })}
                  </Table>
                )}
              </Card>

              {/* Who blocked whom, when, and why. Absent from every table
                  above by construction — a blocklist row is DELETED on
                  unblock, so nothing else here remembers that someone was
                  ever blocked or who lifted it. This is `GET
                  /admin/audit/tenant`, the fleet-wide feed for the tenant
                  domain, which carries every block/unblock action alongside
                  tier changes. */}
              <Card
                title="Who blocked whom"
                note="From the audit log · tenant domain, fleet-wide · block and unblock actions"
              >
                {blockAudit.state.kind === "loading" && <Loading what="the block history" />}
                {blockAudit.state.kind === "signedout" && <SignedOut />}
                {blockAudit.state.kind === "denied" && (
                  <Denied message={blockAudit.state.error.message} />
                )}
                {blockAudit.state.kind === "unavailable" && (
                  <CouldNotLook
                    what="the block history"
                    message={blockAudit.state.error.message}
                    onRetry={blockAudit.reload}
                  />
                )}
                {(blockAudit.state.kind === "invalid" || blockAudit.state.kind === "error") && (
                  <LoadFailed what="the block history" message={blockAudit.state.error.message} />
                )}

                {blockAudit.state.kind === "ready" &&
                  (() => {
                    const rows = eventsOf(blockAudit.state.data).filter(
                      (e) => e.action === "block" || e.action === "unblock",
                    );
                    if (rows.length === 0) {
                      return <Empty>No block or unblock actions in the last 7 days.</Empty>;
                    }
                    return (
                      <Table
                        className="[--gutter:0px]"
                        head={
                          <tr>
                            <Th>When</Th>
                            <Th>Who</Th>
                            <Th>Action</Th>
                            <Th>Target</Th>
                          </tr>
                        }
                      >
                        {rows.slice(0, 10).map((e, i) => (
                          <tr key={`${e.timestamp}-${i}`}>
                            <Td>
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {auditStamp(e.timestamp)}
                              </span>
                            </Td>
                            <Td>
                              <span className="text-xs">{actorEmail(e.actor)}</span>
                            </Td>
                            <Td>
                              <Badge tone={e.action === "block" ? "red" : "green"}>
                                {e.action}
                              </Badge>
                            </Td>
                            <Td>
                              {auditTarget(e.target) ? (
                                <Link
                                  to={`/tenants/${encodeURIComponent(auditTarget(e.target)!)}`}
                                  className="ref text-xs"
                                >
                                  {auditTarget(e.target)}
                                </Link>
                              ) : (
                                <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                              )}
                            </Td>
                          </tr>
                        ))}
                      </Table>
                    );
                  })()}
              </Card>

              <div className="grid gap-4 lg:grid-cols-2">
                <Card
                  title="Block someone everywhere"
                  note={
                    <>
                      Writes a <span className="ref">("*", email)</span> row. This control lives
                      only here, never on a merchant page — the widest block should not be
                      reachable by muscle memory while looking at one merchant.
                    </>
                  }
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Width on a wrapper — F7's Input is a `w-full` span, so a
                        `w-72` on the Input itself never applied. */}
                    <div className="w-72">
                      <Input
                        value={blockEmail}
                        onChange={(ev) => setBlockEmail(ev.target.value)}
                        placeholder="email@example.com"
                        aria-label="Email to block everywhere"
                      />
                    </div>
                    {blockEmail.trim() && (
                      <BlockControl
                        config={config}
                        scope={{ kind: "everywhere", email: blockEmail.trim().toLowerCase() }}
                        blocked={false}
                        onDone={() => {
                          setBlockEmail("");
                          reload();
                        }}
                      />
                    )}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500">
                    Stored lowercase, which is how the server matches it.
                  </p>
                </Card>

                {/* Answers a support question definitively rather than by
                    inference across the three tables above — it reads the same
                    check the running server uses. */}
                <Card
                  title="Would this person be refused right now?"
                  note="Checks all three scopes at once, the same way the running server does."
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="w-40">
                      <Input
                        value={ref}
                        onChange={(e) => setRef(e.target.value)}
                        placeholder="Merchant reference"
                        aria-label="Merchant reference"
                      />
                    </div>
                    <div className="w-64">
                      <Input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="email@example.com"
                        aria-label="Email to check"
                      />
                    </div>
                    {/* The enable check and the URL read the SAME trimmed
                        values. They used to disagree — enabled on `ref.trim()`
                        while the link was built from raw `ref` — so a reference
                        pasted from a spreadsheet with a trailing space enabled
                        the button and resolved to `…/507077%20/users/…`, which
                        404s and reads as "no such merchant" rather than "stray
                        whitespace". */}
                    <Button
                      href={
                        checkRef && checkEmail
                          ? `/tenants/${encodeURIComponent(checkRef)}/users/${encodeURIComponent(
                              checkEmail.toLowerCase(),
                            )}`
                          : undefined
                      }
                      disabled={!checkRef || !checkEmail}
                    >
                      Check
                    </Button>
                  </div>
                </Card>
              </div>

              {/* The design's "What this console cannot do".
                  Not a permission check and not a coming-soon: the blocklist
                  schema accepts ("*","*"), a row denying every user at every
                  merchant, and nothing here composes one. Stating it is the
                  point — an operator who knows the row exists should be able to
                  find out that this console will not write it, rather than
                  discovering it by trying. */}
              <Card title="What this console cannot do" readOnly>
                <p className="text-sm/6">
                  The blocklist accepts a <span className="ref">(&quot;*&quot;,&quot;*&quot;)</span>{" "}
                  row, which would deny every user in every merchant at once. No group reaches it
                  and no dialog here composes one — the capability is <strong>absent</strong>, not
                  permission-checked.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {/* Outline, like the design's own disabled affordance — a
                      `plain` disabled button has no edge, so it reads as text
                      rather than as the control that is being withheld. */}
                  <Button outline type="button" disabled>
                    Block everything
                  </Button>
                  <Badge tone="red">not expressible</Badge>
                </div>
                <p className="mt-2 text-xs/5 text-slate-500 dark:text-slate-400">
                  Absent by construction. Adding it would mean adding a control, not granting an
                  authority — which is the difference this panel exists to record.
                </p>
              </Card>

              {/* The design's second layer. Its suspend flow writes to BOTH
                  products and reports a per-product read-back — "labeeb match ·
                  mcp not attempted" — because either half can succeed alone.
                  Only MCP's half exists here, so the panel is drawn and not
                  built: a button that suspended one product while claiming to
                  suspend the merchant is worse than no button. */}
              <ProductPanel
                id="labeeb"
                title="Suspend the assistant"
                what="Suspend a merchant in Labeeb, with a per-product read-back"
                minHeight={200}
              >
                <Panel className="p-4">
                  <div className="flex flex-col gap-3">
                    {Array.from({ length: 3 }, (_, i) => (
                      <div key={i} className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="h-3 w-44 rounded bg-slate-950/10 dark:bg-white/10" />
                          <div className="mt-1.5 h-2 w-56 rounded bg-slate-950/5 dark:bg-white/5" />
                        </div>
                        <div className="h-7 w-24 rounded-lg bg-slate-950/5 ring-1 ring-slate-950/10 dark:bg-white/5 dark:ring-white/10" />
                      </div>
                    ))}
                  </div>
                </Panel>
              </ProductPanel>
            </>
          );
        })()}
    </Page>
  );
}
